import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from "../components/SEO";
import { PAGE_SEO } from "../seo-data";
import { openBookingModal } from "../components/BookingModal";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, Phone, Database, ArrowRight, MessageSquare, 
  Calendar, CheckCircle2, Bot, Mail, PhoneCall, 
  Filter, UserCheck, Bell, Settings, Play, Pause,
  RotateCcw, ChevronRight, RefreshCw, CalendarX2,
  Star, Moon, Clock, ThumbsUp, Sun, Send
} from 'lucide-react';

const Workflows = () => {
  const [activeWorkflow, setActiveWorkflow] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const workflows = [
    {
      id: 'ads-website',
      title: 'New Leads',
      subtitle: 'From forms, websites & landing pages',
      icon: Zap,
      color: 'pink',
      gradient: 'from-pink-500 to-rose-500',
      glow: 'shadow-pink-500/30',
      steps: [
        { title: 'Lead Opts In', desc: 'Form submission detected', icon: Zap },
        { title: 'Instant AI Text', desc: 'Response in 60 seconds', icon: MessageSquare },
        { title: 'AI Conversation', desc: 'Qualifies & handles objections', icon: Bot },
        { title: 'Books Appointment', desc: 'Calendar link sent', icon: Calendar },
        { title: 'Smart Reminders', desc: 'No-show prevention', icon: Bell },
        { title: 'CRM Updated', desc: 'All data logged', icon: Settings },
      ],
      outcome: 'APPOINTMENT KEPT'
    },
    {
      id: 'inbound-calls',
      title: 'Inbound Calls',
      subtitle: 'Customers calling your business',
      icon: Phone,
      color: 'cyan',
      gradient: 'from-cyan-500 to-blue-500',
      glow: 'shadow-cyan-500/30',
      steps: [
        { title: 'Call Received', desc: 'Customer dials in', icon: PhoneCall },
        { title: 'AI Answers', desc: 'Human-like voice agent', icon: Bot },
        { title: 'Qualifies Lead', desc: 'Asks smart questions', icon: Filter },
        { title: 'Books or Transfers', desc: 'Routes appropriately', icon: Calendar },
        { title: 'Confirmation Sent', desc: 'Text + email follow-up', icon: Mail },
        { title: 'CRM Updated', desc: 'Call recorded & logged', icon: Settings },
      ],
      outcome: 'DEAL CLOSED'
    },
    {
      id: 'old-leads',
      title: 'Dead Leads',
      subtitle: 'Revive your old database',
      icon: Database,
      color: 'purple',
      gradient: 'from-purple-500 to-violet-500',
      glow: 'shadow-purple-500/30',
      steps: [
        { title: 'Upload List', desc: 'Old leads imported', icon: Database },
        { title: 'AI Outreach', desc: 'Personalized SMS waves', icon: MessageSquare },
        { title: 'AI Calls', desc: 'Voice follow-up', icon: PhoneCall },
        { title: 'Lead Scored', desc: 'Hot leads prioritized', icon: UserCheck },
        { title: 'Appointment Set', desc: 'Calendar booked', icon: Calendar },
        { title: 'Deal Tracked', desc: 'Pipeline updated', icon: Settings },
      ],
      outcome: 'REVENUE RECOVERED'
    },
    {
      id: 'no-show-recovery',
      title: 'No-Show Recovery',
      subtitle: 'Re-engage missed appointments',
      icon: CalendarX2,
      color: 'orange',
      gradient: 'from-orange-500 to-amber-500',
      glow: 'shadow-orange-500/30',
      steps: [
        { title: 'No-Show Detected', desc: 'Missed appointment flagged', icon: CalendarX2 },
        { title: 'AI Text Sent', desc: '"Hey, we missed you today!"', icon: MessageSquare },
        { title: 'Reschedule Offered', desc: 'New time slots presented', icon: Calendar },
        { title: 'AI Call Follow-Up', desc: 'Voice outreach if no reply', icon: PhoneCall },
        { title: 'Appointment Rebooked', desc: 'New slot confirmed', icon: CheckCircle2 },
        { title: 'Reminder Sequence', desc: 'Extra reminders to prevent repeat', icon: Bell },
      ],
      outcome: 'APPOINTMENT RECOVERED',
      stats: { speed: '5min', availability: 'Auto', rate: '67%' },
      statsLabels: { speed: 'After No-Show', availability: 'Trigger', rate: 'Recovery Rate' }
    },
    {
      id: 'review-referral',
      title: 'Reviews & Referrals',
      subtitle: 'Turn happy clients into growth',
      icon: Star,
      color: 'green',
      gradient: 'from-emerald-500 to-green-500',
      glow: 'shadow-emerald-500/30',
      steps: [
        { title: 'Deal Closed', desc: 'Service completed', icon: CheckCircle2 },
        { title: 'Review Request', desc: 'Google/Yelp link sent', icon: Star },
        { title: 'AI Follow-Up', desc: 'Gentle reminder if no review', icon: MessageSquare },
        { title: 'Thank You Sent', desc: 'Gratitude message on review', icon: ThumbsUp },
        { title: 'Referral Ask', desc: '"Know anyone who needs us?"', icon: Send },
        { title: 'Referral Tracked', desc: 'New lead added to pipeline', icon: UserCheck },
      ],
      outcome: 'NEW LEADS GENERATED',
      stats: { speed: '24hr', availability: 'Auto', rate: '4.8★' },
      statsLabels: { speed: 'Post-Close', availability: 'Sequence', rate: 'Avg Rating' }
    },
    {
      id: 'after-hours',
      title: 'After-Hours AI',
      subtitle: '24/7 coverage while you sleep',
      icon: Moon,
      color: 'indigo',
      gradient: 'from-indigo-500 to-blue-600',
      glow: 'shadow-indigo-500/30',
      steps: [
        { title: 'After-Hours Call/Text', desc: 'Inquiry outside business hours', icon: Moon },
        { title: 'AI Answers Instantly', desc: 'No missed opportunities', icon: Bot },
        { title: 'Lead Qualified', desc: 'Smart questions asked', icon: Filter },
        { title: 'Next-Day Booking', desc: 'Appointment scheduled', icon: Calendar },
        { title: 'Team Notified', desc: 'Morning summary sent', icon: Sun },
        { title: 'CRM Updated', desc: 'All interactions logged', icon: Settings },
      ],
      outcome: 'ZERO MISSED LEADS',
      stats: { speed: '<10s', availability: '24/7', rate: '100%' },
      statsLabels: { speed: 'Response', availability: 'Coverage', rate: 'Capture Rate' }
    }
  ];

  const currentWorkflow = workflows[activeWorkflow];

  // Auto-play logic
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setActiveStep(prev => {
          if (prev >= currentWorkflow.steps.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1200);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentWorkflow.steps.length]);

  // Start auto-play on load
  useEffect(() => {
    const timer = setTimeout(() => setIsPlaying(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleWorkflowChange = (index) => {
    setActiveWorkflow(index);
    setActiveStep(0);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 300);
  };

  const handleReset = () => {
    setActiveStep(0);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 300);
  };

  const colorClasses = {
    pink: {
      bg: 'bg-gradient-to-br from-pink-500 to-rose-500',
      text: 'text-pink-500',
      border: 'border-pink-500/30',
      glow: 'shadow-[0_0_30px_rgba(236,72,153,0.3)]',
      line: 'bg-gradient-to-b from-pink-500 to-pink-300',
      pulse: 'bg-pink-500',
    },
    cyan: {
      bg: 'bg-gradient-to-br from-cyan-500 to-blue-500',
      text: 'text-cyan-500',
      border: 'border-cyan-500/30',
      glow: 'shadow-[0_0_30px_rgba(6,182,212,0.3)]',
      line: 'bg-gradient-to-b from-cyan-500 to-cyan-300',
      pulse: 'bg-cyan-500',
    },
    purple: {
      bg: 'bg-gradient-to-br from-purple-500 to-violet-500',
      text: 'text-purple-500',
      border: 'border-purple-500/30',
      glow: 'shadow-[0_0_30px_rgba(139,92,246,0.3)]',
      line: 'bg-gradient-to-b from-purple-500 to-purple-300',
      pulse: 'bg-purple-500',
    },
    orange: {
      bg: 'bg-gradient-to-br from-orange-500 to-amber-500',
      text: 'text-orange-500',
      border: 'border-orange-500/30',
      glow: 'shadow-[0_0_30px_rgba(249,115,22,0.3)]',
      line: 'bg-gradient-to-b from-orange-500 to-amber-300',
      pulse: 'bg-orange-500',
    },
    green: {
      bg: 'bg-gradient-to-br from-emerald-500 to-green-500',
      text: 'text-emerald-500',
      border: 'border-emerald-500/30',
      glow: 'shadow-[0_0_30px_rgba(16,185,129,0.3)]',
      line: 'bg-gradient-to-b from-emerald-500 to-green-300',
      pulse: 'bg-emerald-500',
    },
    indigo: {
      bg: 'bg-gradient-to-br from-indigo-500 to-blue-600',
      text: 'text-indigo-500',
      border: 'border-indigo-500/30',
      glow: 'shadow-[0_0_30px_rgba(99,102,241,0.3)]',
      line: 'bg-gradient-to-b from-indigo-500 to-blue-300',
      pulse: 'bg-indigo-500',
    },
  };

  const colors = colorClasses[currentWorkflow.color];

  return (
    <div className="workflows-page-v2" data-testid="workflows-page">
      <SEO {...PAGE_SEO.workflows} />
      {/* Hero */}
      <section className="wf-hero">
        <div className="wf-hero-bg">
          <div className="wf-grid"></div>
          <div className="wf-glow wf-glow-1"></div>
          <div className="wf-glow wf-glow-2"></div>
        </div>
        <div className="container">
          <motion.div 
            className="wf-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="wf-eyebrow">Custom AI Workflows</span>
            <h1 className="wf-title">
              Watch Your Leads <span className="wf-title-gradient">Convert</span>
            </h1>
            <p className="wf-subtitle">
              See exactly how AI handles every touchpoint — from first contact to closed deal.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Workflow Selector */}
      <section className="wf-selector-section">
        <div className="container">
          <div className="wf-selector" data-testid="workflow-tabs">
            {workflows.map((wf, index) => {
              const Icon = wf.icon;
              const isActive = activeWorkflow === index;
              return (
                <motion.button
                  key={wf.id}
                  className={`wf-selector-btn ${isActive ? 'active' : ''} wf-${wf.color}`}
                  onClick={() => handleWorkflowChange(index)}
                  data-testid={`workflow-tab-${wf.id}`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className={`wf-selector-icon ${isActive ? colorClasses[wf.color].bg : ''}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="wf-selector-text">
                    <span className="wf-selector-title">{wf.title}</span>
                    <span className="wf-selector-sub">{wf.subtitle}</span>
                  </div>
                  {isActive && (
                    <motion.div 
                      className="wf-selector-indicator"
                      layoutId="activeTab"
                    />
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Visualization */}
      <section className="wf-visualization">
        <div className="container">
          <div className="wf-journey" data-testid="workflow-flowchart">
            {/* Progress line */}
            <div className="wf-progress-track">
              <motion.div 
                className={`wf-progress-fill ${colors.bg}`}
                initial={{ height: '0%' }}
                animate={{ height: `${((activeStep + 1) / currentWorkflow.steps.length) * 100}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>

            {/* Steps */}
            <div className="wf-steps">
              <AnimatePresence mode="wait">
                {currentWorkflow.steps.map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = index <= activeStep;
                  const isCurrent = index === activeStep;

                  return (
                    <motion.div
                      key={`${currentWorkflow.id}-${index}`}
                      className={`wf-step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                      data-testid={`workflow-step-${index}`}
                    >
                      {/* Step node - stays lit after animation passes */}
                      <div className={`wf-step-node ${isActive ? colors.bg + ' ' + colors.glow : ''}`}>
                        {isActive ? (
                          <StepIcon className="w-5 h-5" />
                        ) : (
                          <span className="wf-step-num">{index + 1}</span>
                        )}
                        
                        {/* Pulse effect for current */}
                        {isCurrent && (
                          <motion.div 
                            className={`wf-pulse ${colors.pulse}`}
                            animate={{ scale: [1, 2], opacity: [0.5, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          />
                        )}
                      </div>

                      {/* Step content */}
                      <motion.div 
                        className="wf-step-content"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ 
                          opacity: isActive ? 1 : 0.3, 
                          x: isActive ? 0 : -10 
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <h4 className={`wf-step-title ${isCurrent ? colors.text : ''}`}>
                          {step.title}
                        </h4>
                        <p className="wf-step-desc">{step.desc}</p>
                      </motion.div>

                      {/* Connector line */}
                      {index < currentWorkflow.steps.length - 1 && (
                        <div className="wf-connector">
                          <motion.div 
                            className={`wf-connector-fill ${colors.line}`}
                            initial={{ height: '0%' }}
                            animate={{ height: isActive ? '100%' : '0%' }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                          />
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {/* Outcome */}
              <motion.div 
                className={`wf-outcome ${activeStep >= currentWorkflow.steps.length - 1 ? 'active' : ''}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ 
                  opacity: activeStep >= currentWorkflow.steps.length - 1 ? 1 : 0.3,
                  scale: activeStep >= currentWorkflow.steps.length - 1 ? 1 : 0.9
                }}
                transition={{ duration: 0.4 }}
              >
                <div className="wf-outcome-badge">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  <span className="wf-outcome-text">{currentWorkflow.outcome}</span>
                </div>
              </motion.div>
            </div>

            {/* AI Follow-up Note */}
            <motion.div 
              className={`wf-followup ${activeStep >= 2 ? 'visible' : ''}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: activeStep >= 2 ? 1 : 0 }}
              transition={{ duration: 0.4 }}
            >
              <RefreshCw className="w-4 h-4" />
              <span>No response? AI follows up automatically via text & calls over days/weeks until booked.</span>
            </motion.div>

            {/* Controls */}
            <div className="wf-controls">
              <button className="wf-control-btn" onClick={handleReset}>
                <RotateCcw className="w-4 h-4" />
              </button>
              <button 
                className={`wf-control-btn wf-play-btn ${isPlaying ? 'playing' : ''}`}
                onClick={() => setIsPlaying(!isPlaying)}
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <span className="wf-step-indicator">
                Step {activeStep + 1} of {currentWorkflow.steps.length}
              </span>
            </div>
          </div>

          {/* Side info card */}
          <motion.div 
            className="wf-info-card"
            key={currentWorkflow.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className={`wf-info-icon ${colors.bg}`}>
              {React.createElement(currentWorkflow.icon, { className: 'w-8 h-8 text-white' })}
            </div>
            <h3 className="wf-info-title">{currentWorkflow.title}</h3>
            <p className="wf-info-desc">{currentWorkflow.subtitle}</p>
            <div className="wf-info-stats">
              <div className="wf-stat">
                <span className="wf-stat-value">{currentWorkflow.stats?.speed || '60s'}</span>
                <span className="wf-stat-label">{currentWorkflow.statsLabels?.speed || 'Response Time'}</span>
              </div>
              <div className="wf-stat">
                <span className="wf-stat-value">{currentWorkflow.stats?.availability || '24/7'}</span>
                <span className="wf-stat-label">{currentWorkflow.statsLabels?.availability || 'Availability'}</span>
              </div>
              <div className="wf-stat">
                <span className="wf-stat-value">{currentWorkflow.stats?.rate || '90%'}</span>
                <span className="wf-stat-label">{currentWorkflow.statsLabels?.rate || 'Automation'}</span>
              </div>
            </div>
            <button
              className="wf-see-it-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('openAIChat'))}
              data-testid={`see-it-action-${currentWorkflow.id}`}
            >
              See it in action <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="wf-cta-section">
        <div className="container">
          <div className="wf-cta-content">
            <h2 className="wf-cta-title">Ready to Automate Your Revenue?</h2>
            <p className="wf-cta-desc">See how this works for your specific business.</p>
            <div className="wf-cta-buttons">
              <button onClick={openBookingModal} className="wf-cta-primary" data-testid="workflows-cta-get-started">
                Get Started <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Workflows;
